import { $permissions } from '@app/containers/store/states';
import { usePageTitle } from '@app/hooks';
import {
    Button,
    Classes,
    Colors,
    Icon,
    Intent,
    Menu,
    MenuItem,
    Popover,
    Spinner,
    Tag,
    Tooltip,
} from '@blueprintjs/core';
import Avatar from '@components/Avatar';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import NonIdealState from '@components/NonIdealState';
import RouterLink from '@components/RouterLink';
import Table from '@components/Table';
import TableCell from '@components/TableCell';
import DevText from '@components/Text';
import { Endpoints, imageHashPreview } from '@data/consts';
import { TeamParticipationRole } from 'dy-frontend-http-repository/lib/data/enums';
import { TeamParticipationResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import { useStore } from 'effector-react';
import React, { useState } from 'react';
import { useRemoveTeamParticipant, useUpdateTeamParticipantRole } from '../../hooks';
import { fetchTeamOccupancy } from '../../store/effects';
import { $team, $teamOccupancy } from '../../store/states';
import { TeamOccupancyParticipantResource } from '../../types';
import findTeamOccupancyParticipant from '../../utils/findTeamOccupancyParticipant';
import { TeamPermission } from 'dy-frontend-permissions/lib/permission';
import OccupancyRate from '@app/containers/components/OccupancyRate';
import moment from 'moment';

const TeamMembers: React.FC = () => {
    const permissions = useStore($permissions);

    usePageTitle('Team Members');

    const team = useStore($team);

    const { removeTeamParticipant } = useRemoveTeamParticipant();
    const { updateTeamParticipantRole } = useUpdateTeamParticipantRole();
    const teamOccupancy = useStore($teamOccupancy);
    const isTeamOccupancyFetching = useStore(fetchTeamOccupancy.pending);

    const [userIdPendingActionResult, setUserIdPendingActionResult] = useState<ID | null>(null);

    if (!team) {
        return (
            <Flex justify="center">
                <Spinner />
            </Flex>
        );
    }

    const memberRoles = [TeamParticipationRole.MEMBER];
    const members = team.participants.filter((participant) => memberRoles.includes(participant.role));

    if (members.length === 0) {
        return (
            <NonIdealState
                icon={<Icon className="mb-2" icon="search" size={70} />}
                title={
                    <Heading className="mb-1" type="h4">
                        Team do not have any members yet
                    </Heading>
                }
            />
        );
    }

    const handleRemoveTeamParticipant = (userId: ID) => {
        setUserIdPendingActionResult(userId);

        removeTeamParticipant({ teamId: team.id, userId })
            .catch((e) => {
                // TODO: handle error
                console.error(e);
            })
            .finally(() => setUserIdPendingActionResult(null));
    };

    const handleUpdateTeamParticipant = (userId: ID, role: TeamParticipationRole) => {
        setUserIdPendingActionResult(userId);

        updateTeamParticipantRole({ teamId: team.id, userId, role })
            .catch((e) => {
                // TODO: handle error
                console.error(e);
            })
            .finally(() => setUserIdPendingActionResult(null));
    };

    const renderTableHeader = () => {
        return (
            <thead>
                <tr>
                    <th>Member</th>
                    <th>Latest clients</th>
                    <th>Active requests</th>
                    <th>Occupancy rate</th>
                    <th>Latest activity</th>
                    <th></th>
                </tr>
            </thead>
        );
    };

    const renderTableBody = () => {
        const renderMemberControlsButton = ({ user }: TeamParticipationResource) => {
            const isManagementAllowed = permissions.isRoot.team || permissions.has(TeamPermission.PARTICIPATION_MANAGE);
            if (!isManagementAllowed) {
                return null;
            }

            const renderMemberControlsMenu = () => {
                const renderPromoteMemberToTeamLeadMenuItem = () => {
                    // Get lead participant
                    const leadParticipant = team.participants.find(
                        (participant) => participant.role === TeamParticipationRole.LEAD
                    );

                    if (leadParticipant) {
                        return null;
                    }

                    return (
                        <MenuItem
                            icon="star"
                            text="Promote to team lead"
                            onClick={() => handleUpdateTeamParticipant(user.id, TeamParticipationRole.LEAD)}
                        />
                    );
                };

                const renderPromoteMemberToTeamManagerMenuItem = () => {
                    return (
                        <MenuItem
                            disabled={userIdPendingActionResult !== null}
                            icon="clean"
                            text="Promote to team manager"
                            onClick={() => handleUpdateTeamParticipant(user.id, TeamParticipationRole.MANAGER)}
                        />
                    );
                };

                const renderRemoveMemberFromTeamMenuItem = () => {
                    return (
                        <MenuItem
                            disabled={userIdPendingActionResult !== null}
                            icon="trash"
                            text="Remove from team"
                            intent={Intent.DANGER}
                            onClick={() => handleRemoveTeamParticipant(user.id)}
                        />
                    );
                };

                return (
                    <Menu>
                        {renderPromoteMemberToTeamLeadMenuItem()}
                        {renderPromoteMemberToTeamManagerMenuItem()}
                        {renderRemoveMemberFromTeamMenuItem()}
                    </Menu>
                );
            };

            return (
                <Popover content={renderMemberControlsMenu()}>
                    <Button
                        disabled={userIdPendingActionResult !== null}
                        loading={userIdPendingActionResult === user.id}
                        minimal
                        icon="more"
                    />
                </Popover>
            );
        };

        const renderTeamUserOccupancy = (participant: TeamOccupancyParticipantResource | null) => {
            // A member added in this session has no score until the refetch lands; the rows that
            // already have one keep showing it rather than all flashing to a skeleton
            if (!teamOccupancy || (isTeamOccupancyFetching && !participant)) {
                return (
                    <>
                        <div className={`mb-small ${Classes.SKELETON}`}>
                            <Tag>Score: 0</Tag>
                        </div>
                    </>
                );
            }

            if (!participant) {
                return <DevText muted>No data yet</DevText>;
            }

            return (
                <>
                    <div className="mb-small">
                        {/* The old tooltip read out task complexity, which is no longer what this
                            number measures. Work-report hours and days are not shown here. */}
                        <Tooltip fill content={<DevText>Based on current-week work reports</DevText>}>
                            <OccupancyRate colorByBand interactive score={participant.score} />
                        </Tooltip>
                    </div>
                </>
            );
        };

        const renderRow = (teamParticipation: TeamParticipationResource) => {
            const { user } = teamParticipation;

            const participant = findTeamOccupancyParticipant(teamOccupancy, team.id, user.id);

            // Get avatar src
            let avatarSrc: string | null = null;
            if (user.image_hash) {
                avatarSrc = imageHashPreview.userImage(user.image_hash, ImageHashPreviewSize.SM);
            }

            console.log('teamParticipation: ', teamParticipation);

            return (
                <tr key={user.id}>
                    {/* User info */}
                    <TableCell verticalAlign="middle">
                        <Flex align="center">
                            <Avatar
                                width="42px"
                                height="42px"
                                className="mr-1"
                                alt={`${user.first_name} ${user.last_name}`}
                                src={avatarSrc}
                            />
                            <Flex direction="column">
                                <Flex align="center">
                                    <RouterLink
                                        className="mr-1"
                                        color={Colors.WHITE}
                                        to={Endpoints.CORE_USER_TASKS.replace(':coreUserId', user.id)}
                                    >
                                        {user.first_name} {user.last_name}
                                    </RouterLink>
                                </Flex>

                                <DevText muted>
                                    {user.company_position.length > 0 ? user.company_position : 'Team member'}
                                </DevText>
                            </Flex>
                        </Flex>
                    </TableCell>

                    {/* TODO: Latest clients */}
                    <TableCell verticalAlign="middle">
                        <DevText muted>No data</DevText>
                    </TableCell>

                    {/* TODO: Active requests */}
                    <TableCell verticalAlign="middle">
                        <DevText muted>No data</DevText>
                    </TableCell>

                    {/* Occupancy */}
                    <TableCell verticalAlign="middle">{renderTeamUserOccupancy(participant)}</TableCell>

                    {/* TODO: Latest activity */}
                    <TableCell verticalAlign="middle">
                        {moment(teamParticipation.user.metadata.latest_task_activity_at).format('D MMM YYYY, HH:mm')}
                    </TableCell>

                    {/* Action */}
                    <TableCell verticalAlign="middle">{renderMemberControlsButton(teamParticipation)}</TableCell>
                </tr>
            );
        };

        return <tbody>{members.map(renderRow)}</tbody>;
    };

    return (
        <Table striped>
            {renderTableHeader()}
            {renderTableBody()}
        </Table>
    );
};

export default TeamMembers;
